import React, { useEffect, useRef, useState } from 'react';
import { Icon } from 'semantic-ui-react';
import { useComponentConfigClient } from './componentConfig.client';
import { Activity, X } from 'lucide-react';
const PAGE_SIZE = 20;
// ─── Helpers ──────────────────────────────────────────────────────────────────
const INPUT_TYPE_LABELS = {
    audio: 'Audio',
    image: 'Image',
    pdf: 'PDF',
    text: 'Text',
};
const INPUT_TYPE_ICONS = {
    audio: 'microphone',
    image: 'camera',
    pdf: 'file pdf outline',
    text: 'comment outline',
};
function getOutputPreview(interaction) {
    const raw = interaction.transcription ||
        interaction.extractedText ||
        interaction.chatResponse ||
        (interaction.structuredOutput ? JSON.stringify(interaction.structuredOutput) : null);
    if (!raw)
        return '—';
    return raw.length > 100 ? raw.slice(0, 97) + '...' : raw;
}
function getFullOutput(interaction) {
    return (interaction.transcription ||
        interaction.extractedText ||
        interaction.chatResponse ||
        (interaction.structuredOutput ? JSON.stringify(interaction.structuredOutput, null, 2) : null));
}
function formatTs(ts) {
    return new Date(ts).toLocaleString();
}
const FilePreview = ({ inputType, signedUrl, fileName }) => {
    if (inputType === 'audio') {
        return (React.createElement("div", { className: "bg-gray-900 rounded-lg p-4 flex flex-col items-center gap-3" },
            fileName && React.createElement("p", { className: "text-xs text-gray-400 truncate max-w-full", title: fileName }, fileName),
            React.createElement("audio", { controls: true, src: signedUrl, className: "w-full", style: { colorScheme: 'dark' } }, "Your browser does not support audio playback.")));
    }
    if (inputType === 'image') {
        return (React.createElement("div", { className: "bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center", style: { maxHeight: 360 } },
            React.createElement("img", { src: signedUrl, alt: fileName || 'Input image', className: "max-w-full max-h-80 object-contain" })));
    }
    if (inputType === 'pdf') {
        return (React.createElement("div", { className: "rounded-lg overflow-hidden border border-gray-200", style: { height: 420 } },
            React.createElement("iframe", { src: signedUrl, title: fileName || 'Input PDF', className: "w-full h-full", style: { border: 'none' } })));
    }
    return null;
};
const InteractionDetailModal = ({ interaction, componentId, onClose }) => {
    const client = useComponentConfigClient();
    const [signedUrl, setSignedUrl] = useState(null);
    const [fileLoading, setFileLoading] = useState(false);
    const [fileError, setFileError] = useState(false);
    const overlayRef = useRef(null);
    useEffect(() => {
        if (!interaction.inputFileUri)
            return;
        setFileLoading(true);
        client
            .getInteractionFileUrl(componentId, interaction.interactionId)
            .then(setSignedUrl)
            .catch(() => setFileError(true))
            .finally(() => setFileLoading(false));
    }, [interaction.interactionId]);
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape')
            onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    const fullOutput = getFullOutput(interaction);
    const isJson = !!interaction.structuredOutput && !interaction.transcription && !interaction.extractedText && !interaction.chatResponse;
    return (React.createElement("div", { ref: overlayRef, className: "fixed inset-0 z-50 flex items-center justify-center bg-black/50", onClick: e => { if (e.target === overlayRef.current)
            onClose(); } },
        React.createElement("div", { className: "bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col overflow-hidden", style: { maxHeight: '90vh' } },
            React.createElement("div", { className: "flex items-center justify-between px-6 py-4 border-b border-gray-100" },
                React.createElement("div", { className: "flex items-center gap-3" },
                    React.createElement(Icon, { name: INPUT_TYPE_ICONS[interaction.inputType], size: "large", className: "text-gray-500" }),
                    React.createElement("div", null,
                        React.createElement("p", { className: "font-semibold text-gray-800" },
                            INPUT_TYPE_LABELS[interaction.inputType] || interaction.inputType,
                            " Interaction"),
                        React.createElement("p", { className: "text-xs text-gray-400" },
                            formatTs(interaction.createdAt),
                            " \u00B7 ",
                            interaction.latencyMs,
                            "ms"))),
                React.createElement("button", { onClick: onClose, className: "text-gray-400 hover:text-gray-600 transition-colors" },
                    React.createElement(X, { size: 20 }))),
            React.createElement("div", { className: "overflow-y-auto flex-1 px-6 py-5 space-y-5" },
                interaction.inputFileUri && (React.createElement("div", null,
                    React.createElement("p", { className: "text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" }, "Input File"),
                    fileLoading && (React.createElement("div", { className: "flex items-center gap-2 text-sm text-gray-400 py-6 justify-center" },
                        React.createElement(Icon, { name: "spinner", loading: true }),
                        " Loading preview...")),
                    fileError && (React.createElement("p", { className: "text-sm text-red-400" }, "Failed to load file preview.")),
                    signedUrl && (React.createElement("div", { className: "space-y-2" },
                        React.createElement(FilePreview, { inputType: interaction.inputType, signedUrl: signedUrl, fileName: interaction.inputMetadata?.fileName }),
                        React.createElement("div", { className: "flex items-center justify-between text-xs text-gray-400" },
                            React.createElement("span", null,
                                interaction.inputMetadata?.fileName || '—',
                                interaction.inputMetadata?.mimeType && ` · ${interaction.inputMetadata.mimeType}`,
                                interaction.inputMetadata?.sizeBytes && ` · ${Math.round(interaction.inputMetadata.sizeBytes / 1024)} KB`),
                            React.createElement("a", { href: signedUrl, target: "_blank", rel: "noopener noreferrer", className: "text-orange-500 hover:underline" }, "Open in new tab \u2197")))))),
                interaction.chatMessage && (React.createElement("div", null,
                    React.createElement("p", { className: "text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" }, "User Message"),
                    React.createElement("div", { className: "bg-gray-50 rounded-lg px-4 py-3 text-sm text-gray-800 whitespace-pre-wrap break-words" }, interaction.chatMessage))),
                fullOutput && (React.createElement("div", null,
                    React.createElement("p", { className: "text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" }, interaction.transcription ? 'Transcription' :
                        interaction.extractedText ? 'Extracted Text' :
                            interaction.chatResponse ? 'Agent Response' :
                                'Structured Output'),
                    React.createElement("pre", { className: `bg-gray-50 rounded-lg px-4 py-3 text-sm text-gray-800 whitespace-pre-wrap break-words overflow-y-auto ${isJson ? 'font-mono' : ''}`, style: { maxHeight: 320 } }, fullOutput))),
                (interaction.formKey || interaction.submissionId) && (React.createElement("div", null,
                    React.createElement("p", { className: "text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" }, "Form"),
                    React.createElement("div", { className: "text-sm text-gray-600 space-y-1" },
                        interaction.formKey && React.createElement("p", null,
                            "Form key: ",
                            React.createElement("code", { className: "bg-gray-100 px-1 rounded" }, interaction.formKey)),
                        interaction.submissionId && React.createElement("p", null,
                            "Submission: ",
                            React.createElement("code", { className: "bg-gray-100 px-1 rounded text-xs" }, interaction.submissionId))))),
                !fullOutput && !interaction.inputFileUri && (React.createElement("p", { className: "text-sm text-gray-400 text-center py-4" }, "No output data recorded for this interaction."))))));
};
const MonitoringTab = ({ componentId }) => {
    const client = useComponentConfigClient();
    const [interactions, setInteractions] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    useEffect(() => {
        setLoading(true);
        client
            .listInteractions(componentId, { limit: PAGE_SIZE, offset: page * PAGE_SIZE })
            .then(({ interactions: rows, total: t }) => {
            setInteractions(rows);
            setTotal(t);
        })
            .finally(() => setLoading(false));
    }, [componentId, page]);
    const totalPages = Math.ceil(total / PAGE_SIZE);
    if (loading) {
        return (React.createElement("div", { className: "flex items-center justify-center py-16 text-gray-400" },
            React.createElement(Icon, { name: "spinner", loading: true }),
            " Loading interactions..."));
    }
    if (interactions.length === 0) {
        return (React.createElement("div", { className: "py-16 text-center text-gray-400" },
            React.createElement(Activity, { className: "w-10 h-10 mx-auto mb-3 opacity-30" }),
            React.createElement("p", { className: "text-lg font-medium text-gray-500" }, "No interactions yet"),
            React.createElement("p", { className: "text-sm mt-1" },
                "Pass ",
                React.createElement("code", { className: "bg-gray-100 px-1 rounded" },
                    "componentId=\"",
                    componentId,
                    "\""),
                " to start logging.")));
    }
    return (React.createElement("div", null,
        React.createElement("div", { className: "flex items-center justify-between mb-4" },
            React.createElement("span", { className: "text-sm text-gray-500" },
                total,
                " total interaction",
                total !== 1 ? 's' : '')),
        React.createElement("div", { className: "bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" },
            React.createElement("div", { className: "grid grid-cols-12 gap-3 px-5 py-3 bg-gray-700 text-white text-sm font-medium" },
                React.createElement("div", { className: "col-span-3" }, "TIMESTAMP"),
                React.createElement("div", { className: "col-span-2" }, "INPUT TYPE"),
                React.createElement("div", { className: "col-span-1" }, "LATENCY"),
                React.createElement("div", { className: "col-span-5" }, "OUTPUT PREVIEW"),
                React.createElement("div", { className: "col-span-1" }, "FORM")),
            React.createElement("div", { className: "divide-y divide-gray-100" }, interactions.map((r) => (React.createElement("div", { key: r.interactionId, className: "grid grid-cols-12 gap-3 px-5 py-3 items-center hover:bg-orange-50 cursor-pointer transition-colors", onClick: () => setSelected(r), title: "Click to view details" },
                React.createElement("div", { className: "col-span-3 text-sm text-gray-600" }, formatTs(r.createdAt)),
                React.createElement("div", { className: "col-span-2" },
                    React.createElement("span", { className: "inline-flex items-center gap-1 text-sm text-gray-700" },
                        React.createElement(Icon, { name: INPUT_TYPE_ICONS[r.inputType], size: "small" }),
                        INPUT_TYPE_LABELS[r.inputType] || r.inputType)),
                React.createElement("div", { className: "col-span-1 text-sm font-mono text-gray-700" },
                    r.latencyMs,
                    "ms"),
                React.createElement("div", { className: "col-span-5 text-sm text-gray-500 truncate" }, getOutputPreview(r)),
                React.createElement("div", { className: "col-span-1 text-xs text-gray-400 truncate" }, r.formKey || '—')))))),
        totalPages > 1 && (React.createElement("div", { className: "flex items-center justify-between mt-4" },
            React.createElement("button", { disabled: page === 0, onClick: () => setPage(p => p - 1), className: "px-3 py-1.5 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed" }, "\u2190 Previous"),
            React.createElement("span", { className: "text-sm text-gray-500" },
                "Page ",
                page + 1,
                " of ",
                totalPages),
            React.createElement("button", { disabled: page >= totalPages - 1, onClick: () => setPage(p => p + 1), className: "px-3 py-1.5 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed" }, "Next \u2192"))),
        selected && (React.createElement(InteractionDetailModal, { interaction: selected, componentId: componentId, onClose: () => setSelected(null) }))));
};
export default MonitoringTab;
